/**
 * Zeilendialog der Stoffliste (ELiA-Formular 3.5 und Folgeformulare)
 *
 * ELiA führt einen Stoff als eine Zeile mit aufschaltbaren Blöcken: Vier Kennzeichen entscheiden,
 * welche Folgeformulare er erzeugt, und erst dann sind deren Felder überhaupt auszufüllen.
 * Genau so ist der Dialog gebaut – die Blöcke erscheinen, sobald ihr Kennzeichen gesetzt ist:
 *
 *   Gefahrstoff          → 7.2  Verwendung, Lagerung, Kennzeichnung
 *   Wassergefährdend     → 11.1 Einstufung, Aggregatzustand, WGK, Dichte
 *   Entstehender Abfall  → 9.1  Entsorgungsverfahren, Konsistenz, Anfallhäufigkeit, Jahresmenge
 *   REACH-relevant       → 15.1 Nummern und Einstufungen aus dem Sicherheitsdatenblatt
 *
 * AVV-Schlüssel und CAS-Nummer stammen aus Katalogen mit 842 bzw. 3.117 Einträgen und werden
 * deshalb gesucht statt geladen; ELiA hält es mit seinen Auswahlkatalogen genauso.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import DialogTemplate, { DialogSection } from "../../../layout/DialogTemplate";
import Button from "../../../common/inputs/Button";
import InputField from "../../../common/inputs/InputField";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import InputFieldMultiline from "../../../common/inputs/InputFieldMultiline";
import Dropdown from "../../../common/inputs/Dropdown";
import ToggleChip from "../../../common/inputs/ToggleChip";
import {
  anlagentypenApi,
  StoffCreate,
  StoffFelder,
} from "../../../../services/api/anlagentypen";
import { WertelistenOptions, Option } from "../hooks/useWertelisten";

type Werte = Partial<StoffFelder>;

const LEER: Werte = {
  bezeichnung: "",
  ist_gefahrstoff: false,
  ist_wassergefaehrdend: false,
  ist_entstehender_abfall: false,
  ist_reach_relevant: false,
};

interface StoffDialogProps {
  open: boolean;
  /** Vorhandener Stoff beim Bearbeiten, `null` beim Anlegen */
  stoff: Werte | null;
  wertelisten: WertelistenOptions;
  onClose: () => void;
  onSubmit: (werte: StoffCreate) => Promise<void>;
}

/** Feldraster innerhalb eines Dialogabschnitts – zwei Spalten ab sm */
const Raster: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
      gap: 2,
      mt: 1,
    }}
  >
    {children}
  </Box>
);

const StoffDialog: React.FC<StoffDialogProps> = ({
  open,
  stoff,
  wertelisten,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<Werte>(LEER);
  const [saving, setSaving] = useState(false);
  const [fehler, setFehler] = useState<string>("");
  const [avvTreffer, setAvvTreffer] = useState<Option[]>([]);
  const [casTreffer, setCasTreffer] = useState<Option[]>([]);

  useEffect(() => {
    if (open) {
      setForm(stoff ? { ...stoff } : LEER);
      setFehler("");
    }
  }, [open, stoff]);

  const setzen = useCallback(
    (feld: string, wert: string | number | boolean | null) =>
      setForm((vorher) => ({ ...vorher, [feld]: wert })),
    [],
  );

  const zahl = (feld: keyof Werte) => {
    const wert = form[feld];
    return wert === null || wert === undefined ? "" : String(wert);
  };
  const kennzeichen = (feld: keyof Werte) => form[feld] === true;

  const sucheAvv = async (begriff: string) => {
    const treffer = await anlagentypenApi.sucheAvv(begriff);
    setAvvTreffer(
      treffer.map((t) => ({ value: t.id, label: `${t.code} – ${t.name}` })),
    );
  };

  const sucheCas = async (begriff: string) => {
    const treffer = await anlagentypenApi.sucheCas(begriff);
    setCasTreffer(
      treffer.map((t) => ({
        value: t.id,
        label: t.cas_nummer ? `${t.stoffname} (${t.cas_nummer})` : t.stoffname,
      })),
    );
  };

  const speichern = async () => {
    if (!form.bezeichnung?.trim()) {
      setFehler("Ohne Bezeichnung ist die Zeile in ELiA nicht zuzuordnen.");
      return;
    }
    setSaving(true);
    try {
      await onSubmit({ ...form, bezeichnung: form.bezeichnung.trim() });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogTemplate
      open={open}
      onClose={onClose}
      title={stoff ? "Stoff bearbeiten" : "Stoff anlegen"}
      maxWidth="md"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={speichern} disabled={saving}>
            Speichern
          </Button>
        </>
      }
    >
      <DialogSection title="Allgemeine Informationen" first>
        <Raster>
          <InputField
            label="Bezeichnung des Stoffes/Gemisches"
            value={form.bezeichnung ?? ""}
            onChange={(v) => {
              setzen("bezeichnung", v);
              if (fehler) setFehler("");
            }}
            error={fehler || undefined}
            required
            fullWidth
          />
          <InputField
            label="Komponentenname"
            value={form.komponentenname ?? ""}
            onChange={(v) => setzen("komponentenname", v)}
            fullWidth
            tooltip="Der Handelsname des Betriebsmittels, z. B. „Antifrogen N“ oder „Shell Tellus S4 VX 32“."
          />
          <InputFieldNumber
            label="Gesamtmenge"
            value={zahl("gesamtmenge")}
            onChange={(v) => setzen("gesamtmenge", v)}
            variant="floating"
            tooltip="Die Menge, die sich maximal in der Gesamtanlage befinden kann."
          />
          <Dropdown
            label="Einheit"
            value={form.einheit_id ?? ""}
            options={wertelisten.einheit}
            onChange={(v) => setzen("einheit_id", v || null)}
            variant="floating"
          />
          <Dropdown
            label="CAS-Nummer"
            value={form.cas_stoff_id ?? ""}
            options={casTreffer}
            onChange={(v) => setzen("cas_stoff_id", v || null)}
            searchable
            onSearchInput={(text) => void sucheCas(text)}
            variant="floating"
            tooltip="Der Katalog hat 3.117 Einträge und wird deshalb gesucht statt geladen – Tippen sucht über Stoffname und Nummer, angezeigt werden höchstens 50 Treffer."
          />
          <Dropdown
            label="AVV-Nummer"
            value={form.avv_abfallschluessel_id ?? ""}
            options={avvTreffer}
            onChange={(v) => setzen("avv_abfallschluessel_id", v || null)}
            searchable
            onSearchInput={(text) => void sucheAvv(text)}
            variant="floating"
            tooltip="Tippen sucht über Abfallschlüssel und Bezeichnung."
          />
          <InputFieldNumber
            label="Anteil"
            value={zahl("anteil_prozent")}
            onChange={(v) => setzen("anteil_prozent", v)}
            suffix="%"
            variant="floating"
          />
          <InputField
            label="Art des Produktes"
            value={form.art_des_produktes ?? ""}
            onChange={(v) => setzen("art_des_produktes", v)}
            fullWidth
            placeholder="bei Windenergie: Einsatzstoff"
          />
          <InputFieldNumber
            label="Min. Anteil"
            value={zahl("min_anteil_prozent")}
            onChange={(v) => setzen("min_anteil_prozent", v)}
            suffix="%"
            variant="floating"
          />
          <InputFieldNumber
            label="Max. Anteil"
            value={zahl("max_anteil_prozent")}
            onChange={(v) => setzen("max_anteil_prozent", v)}
            suffix="%"
            variant="floating"
          />
          <InputFieldMultiline
            label="Bemerkung / Erläuterung"
            value={form.bemerkung ?? ""}
            onChange={(v) => setzen("bemerkung", v)}
            fullWidth
            tooltip="ELiA: „Insbesondere sind bei Gemischen (z. B. Abfällen) die Gefährlichkeitsmerkmale anzugeben.“ In den Belegen steht hier der Einbauort oder die Phase."
          />
        </Raster>
      </DialogSection>

      <DialogSection title="Eigenschaften – jede schaltet ein Folgeformular frei">
        <Raster>
          <ToggleChip
            value={kennzeichen("ist_gefahrstoff")}
            activeLabel="Gefahrstoff"
            inactiveLabel="Gefahrstoff"
            onToggle={() =>
              setzen("ist_gefahrstoff", !kennzeichen("ist_gefahrstoff"))
            }
          />
          <ToggleChip
            value={kennzeichen("ist_wassergefaehrdend")}
            activeLabel="Wassergefährdend"
            inactiveLabel="Wassergefährdend"
            onToggle={() =>
              setzen(
                "ist_wassergefaehrdend",
                !kennzeichen("ist_wassergefaehrdend"),
              )
            }
          />
          <ToggleChip
            value={kennzeichen("ist_entstehender_abfall")}
            activeLabel="Entstehender Abfall"
            inactiveLabel="Entstehender Abfall"
            onToggle={() =>
              setzen(
                "ist_entstehender_abfall",
                !kennzeichen("ist_entstehender_abfall"),
              )
            }
          />
          <ToggleChip
            value={kennzeichen("ist_reach_relevant")}
            activeLabel="REACH-relevant"
            inactiveLabel="REACH-relevant"
            onToggle={() =>
              setzen("ist_reach_relevant", !kennzeichen("ist_reach_relevant"))
            }
          />
        </Raster>
      </DialogSection>

      {kennzeichen("ist_gefahrstoff") && (
        <DialogSection title="Gefahrstoff">
          <Raster>
            <InputFieldNumber
              label="Verwendung / Verbrauch"
              value={zahl("verwendung_kg_h")}
              onChange={(v) => setzen("verwendung_kg_h", v)}
              suffix="kg/h"
              variant="floating"
            />
            <InputFieldNumber
              label="Lagerung"
              value={zahl("lagerung_kg")}
              onChange={(v) => setzen("lagerung_kg", v)}
              suffix="kg"
              variant="floating"
            />
            <InputField
              label="Kennzeichnung"
              value={form.kennzeichnung ?? ""}
              onChange={(v) => setzen("kennzeichnung", v)}
              fullWidth
            />
          </Raster>
        </DialogSection>
      )}

      {kennzeichen("ist_wassergefaehrdend") && (
        <DialogSection title="Wassergefährdend">
          <Raster>
            <Dropdown
              label="Einstufung"
              value={form.awsv_einstufung_id ?? ""}
              options={wertelisten.awsv_einstufung}
              onChange={(v) => setzen("awsv_einstufung_id", v || null)}
              variant="floating"
            />
            <InputField
              label="Wassergefährdungsklasse"
              value={form.wassergefaehrdungsklasse ?? ""}
              onChange={(v) => setzen("wassergefaehrdungsklasse", v)}
              fullWidth
              placeholder="z. B. WGK 1"
            />
            <Dropdown
              label="Aggregatzustand"
              value={form.aggregatzustand_id ?? ""}
              options={wertelisten.aggregatzustand}
              onChange={(v) => setzen("aggregatzustand_id", v || null)}
              variant="floating"
            />
            <Dropdown
              label="Art des Umgangs"
              value={form.umgang_art_id ?? ""}
              options={wertelisten.umgang_art}
              onChange={(v) => setzen("umgang_art_id", v || null)}
              variant="floating"
            />
            <InputFieldNumber
              label="Dichte"
              value={zahl("dichte_g_cm3")}
              onChange={(v) => setzen("dichte_g_cm3", v)}
              suffix="g/cm³"
              variant="floating"
            />
          </Raster>
        </DialogSection>
      )}

      {kennzeichen("ist_entstehender_abfall") && (
        <DialogSection title="Entstehender Abfall">
          <Raster>
            <Dropdown
              label="Entsorgungsverfahren"
              value={form.entsorgungsverfahren_id ?? ""}
              options={wertelisten.entsorgungsverfahren}
              onChange={(v) => setzen("entsorgungsverfahren_id", v || null)}
              variant="floating"
            />
            <InputField
              label="Konsistenz"
              value={form.konsistenz ?? ""}
              onChange={(v) => setzen("konsistenz", v)}
              fullWidth
              placeholder="fest, flüssig, stichfest, staubförmig"
              tooltip="Freitext: Die ELiA-Werteliste für Aggregatzustände kennt nur fest, flüssig und gasförmig – die Konsistenz eines Abfalls hat mehr Ausprägungen."
            />
            <InputField
              label="Anfallhäufigkeit"
              value={form.anfallhaeufigkeit_pro_jahr ?? ""}
              onChange={(v) => setzen("anfallhaeufigkeit_pro_jahr", v)}
              fullWidth
              tooltip="„k“ bei kontinuierlichem Anfall, sonst die Anzahl pro Jahr – beim Ölwechsel also das Wartungsintervall."
            />
            <InputFieldNumber
              label="Jahresmenge"
              value={zahl("jahresmenge_t")}
              onChange={(v) => setzen("jahresmenge_t", v)}
              suffix="t/a"
              variant="floating"
            />
          </Raster>
        </DialogSection>
      )}

      {kennzeichen("ist_reach_relevant") && (
        <DialogSection title="REACH – Angaben aus dem Sicherheitsdatenblatt">
          <Raster>
            <Dropdown
              label="REACH-Rolle"
              value={form.reach_rolle_id ?? ""}
              options={wertelisten.reach_rolle}
              onChange={(v) => setzen("reach_rolle_id", v || null)}
              variant="floating"
            />
            <InputFieldNumber
              label="Jahresmenge"
              value={zahl("reach_jahresmenge_t")}
              onChange={(v) => setzen("reach_jahresmenge_t", v)}
              suffix="t/a"
              variant="floating"
            />
            <InputField
              label="EG-Nummer"
              value={form.eg_nummer ?? ""}
              onChange={(v) => setzen("eg_nummer", v)}
              fullWidth
            />
            <InputField
              label="Index-Nummer"
              value={form.index_nummer ?? ""}
              onChange={(v) => setzen("index_nummer", v)}
              fullWidth
              tooltip="EG-Index-Nummer nach Anhang VI der CLP-Verordnung, nur für harmonisiert eingestufte Stoffe."
            />
            <InputField
              label="REG-Nummer"
              value={form.reg_nummer ?? ""}
              onChange={(v) => setzen("reg_nummer", v)}
              fullWidth
              tooltip="Registriernummer des Lieferanten – nach REACH ist er verpflichtet, sie mitzuteilen."
            />
          </Raster>
          <Raster>
            <ToggleChip
              value={kennzeichen("ist_registrierter_stoff")}
              activeLabel="Registrierter Stoff"
              inactiveLabel="Registrierter Stoff"
              onToggle={() =>
                setzen(
                  "ist_registrierter_stoff",
                  !kennzeichen("ist_registrierter_stoff"),
                )
              }
            />
            <ToggleChip
              value={kennzeichen("ist_anhang_xiv")}
              activeLabel="Nach Anhang XIV"
              inactiveLabel="Nach Anhang XIV"
              onToggle={() =>
                setzen("ist_anhang_xiv", !kennzeichen("ist_anhang_xiv"))
              }
            />
            <ToggleChip
              value={kennzeichen("ist_anhang_xvii")}
              activeLabel="Nach Anhang XVII"
              inactiveLabel="Nach Anhang XVII"
              onToggle={() =>
                setzen("ist_anhang_xvii", !kennzeichen("ist_anhang_xvii"))
              }
            />
            <ToggleChip
              value={kennzeichen("ist_kandidatenstoff")}
              activeLabel="Kandidatenstoff"
              inactiveLabel="Kandidatenstoff"
              onToggle={() =>
                setzen(
                  "ist_kandidatenstoff",
                  !kennzeichen("ist_kandidatenstoff"),
                )
              }
            />
            <ToggleChip
              value={kennzeichen("ist_nanoskalig")}
              activeLabel="Nanoskalig"
              inactiveLabel="Nanoskalig"
              onToggle={() =>
                setzen("ist_nanoskalig", !kennzeichen("ist_nanoskalig"))
              }
            />
            <ToggleChip
              value={kennzeichen("ist_identifizierte_verwendung")}
              activeLabel="Identifizierte Verwendung"
              inactiveLabel="Identifizierte Verwendung"
              onToggle={() =>
                setzen(
                  "ist_identifizierte_verwendung",
                  !kennzeichen("ist_identifizierte_verwendung"),
                )
              }
            />
          </Raster>
        </DialogSection>
      )}
    </DialogTemplate>
  );
};

export default StoffDialog;
